import { useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useAppStore } from '../stores/appStore'
import { Button } from '@geiertime/ui'
import { Menu, X } from 'lucide-react'
import { motion, useScroll, useMotionValueEvent, useReducedMotion } from 'motion/react'

/**
 * Navigation links configuration with Italian labels.
 */
const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/downloads', label: 'Download' },
  { to: '/faq', label: 'Domande Frequenti' },
]

/**
 * Responsive navigation bar component.
 * Displays logo, navigation links, and mobile hamburger menu.
 */
export function Navbar() {
  const { isMobileMenuOpen, toggleMobileMenu, closeMobileMenu } = useAppStore()
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname
  const shouldReduceMotion = useReducedMotion()

  const [isScrolled, setIsScrolled] = useState(false)
  const { scrollY } = useScroll()
  useMotionValueEvent(scrollY, 'change', (latest) => {
    setIsScrolled(latest > 8)
  })

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b backdrop-blur transition-all duration-300 ${
        isScrolled
          ? 'border-border bg-background/95 shadow-sm supports-[backdrop-filter]:bg-background/85'
          : 'border-transparent bg-background/70 supports-[backdrop-filter]:bg-background/40'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 text-xl font-bold text-foreground"
          onClick={closeMobileMenu}
        >
          <span className="text-primary">Geier</span>Time
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => {
            const isActive = currentPath === link.to
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`relative pb-1 text-sm font-medium transition-colors hover:text-primary ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {link.label}
                {isActive &&
                  (shouldReduceMotion ? (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-gradient-to-r from-primary to-secondary" />
                  ) : (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-gradient-to-r from-primary to-secondary"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  ))}
              </Link>
            )
          })}
          <Button asChild variant="default" size="sm">
            <Link to="/checkout/$planId" params={{ planId: 'basic' }}>Acquista Ora</Link>
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          onClick={toggleMobileMenu}
          aria-label={isMobileMenuOpen ? 'Chiudi menu' : 'Apri menu'}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-background">
          <div className="space-y-1 px-4 py-4">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block rounded-md px-3 py-2 text-base font-medium transition-colors hover:bg-accent ${
                  currentPath === link.to
                    ? 'text-primary bg-accent'
                    : 'text-muted-foreground'
                }`}
                onClick={closeMobileMenu}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2">
              <Button asChild className="w-full" size="sm">
                <Link
                  to="/checkout/$planId"
                  params={{ planId: 'basic' }}
                  onClick={closeMobileMenu}
                >
                  Acquista Ora
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
